/**
 * @fileoverview Rendering one extension.
 *
 * Four outcomes, and keeping them distinct is the whole job:
 *
 *   no definition        → the URL names a page that does not exist
 *   not discoverable     → the page exists but not for this deployment, which
 *                          the viewer must not be able to tell apart from the
 *                          above; saying "you lack permission" for a page that
 *                          is merely switched off leaks the deployment's shape
 *   discoverable, locked → the page exists, they may not open it, and SAYING SO
 *                          is the point — an administrator who cannot see what
 *                          they would need to be granted cannot ask for it
 *   visible              → render it
 *
 * The lock case is why `isVisible` and `isDiscoverable` are separate fields
 * rather than one boolean: recovered behaviour is that All Settings "derives
 * locked from isVisible and filters by isDiscoverable".
 */

import { Suspense, lazy, useMemo, type ComponentType } from "react";
import { NonIdealState } from "@blueprintjs/core";

import type { HydratedExtension } from "../registry/ExtensionDefinition";
import { LoadingBarTrigger } from "./LoadingBar";

export interface ExtensionHostProps {
  readonly extension: HydratedExtension | undefined;
  readonly slug: string;
}

export function ExtensionHost({ extension, slug }: ExtensionHostProps): React.ReactElement {
  /*
    `lazy()` must be called with a stable factory or React remounts the page on
    every render — and remounting a settings form discards what someone typed.
    Keyed on the id rather than the object because hydration returns fresh
    objects each pass.
  */
  const Loaded = useMemo<ComponentType | null>(
    () =>
      extension && extension.isVisible && extension.definition.component
        ? lazy(extension.definition.component as () => Promise<{ default: ComponentType }>)
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- identity by id, see above
    [extension?.definition.id, extension?.isVisible],
  );

  if (!extension || !extension.isDiscoverable) {
    return (
      <NonIdealState
        icon="search"
        title="Page not found"
        description={`No Control Panel page is registered at "${slug}".`}
      />
    );
  }

  if (!extension.isVisible || !Loaded) {
    return (
      <NonIdealState
        icon="lock"
        title={extension.definition.displayName}
        description={
          extension.reason?.startsWith("missing required operations")
            ? `This page requires ${extension.definition.visibility.conjunctiveOperations.join(", ")}. Ask an administrator to grant it.`
            : extension.isImplemented === false
              // Said plainly. "Not available" reads as a permission problem and
              // sends an administrator to ask for a grant that would not help.
              ? `${extension.definition.displayName} is part of the platform but is not built in this deployment yet.`
              : (extension.reason ?? "This page is not available.")
        }
      />
    );
  }

  return (
    /*
      The fallback reports the load to the frame's bar rather than drawing a
      spinner here. A spinner in the content area blanks the page you are
      leaving for the length of a chunk fetch; the bar leaves it in place.
    */
    <Suspense fallback={<LoadingBarTrigger />}>
      <Loaded />
    </Suspense>
  );
}
